import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromTablesPlan from '../reducers/tables-plan.reducer';
import { selectReservationListState } from './reservation-list.selectors';
import { Common } from 'src/app/lib/common-methods';

export const selectTablesPlanState = createFeatureSelector<fromTablesPlan.State>(
  fromTablesPlan.tablesPlanFeatureKey
);

export const selectEditMode = createSelector(selectTablesPlanState, state => state.editMode);
export const selectReservationAssignTablesMode = createSelector(selectTablesPlanState, state => state.reservationAssignTablesMode);
export const selectAddJoinedTablesMode = createSelector(selectTablesPlanState, state => state.addJoinedTablesMode);
export const selectAssignCategoriesToTablesMode = createSelector(selectTablesPlanState, state => state.assignCategoriesToTablesMode);
export const selectSelectedOnlineCategoryToAssign = createSelector(selectTablesPlanState, state => state.selectedOnlineCategoryToAssign);
export const selectExcludeFromOnlineChannelsMode = createSelector(selectTablesPlanState, state => state.excludeFromOnlineChannelsMode);
export const selectIsOnExcludeFromOnlineChannelsMode = createSelector(selectTablesPlanState, state => state.excludeFromOnlineChannelsMode !== '');
export const selectConflictingTables = createSelector(selectTablesPlanState, state => state.conflictingTables);
export const selectConflictingReservations = createSelector(selectTablesPlanState, state => state.conflictingReservations);
export const selectSwapTableNames = createSelector(selectTablesPlanState, state => state.swapTableNames);
export const selectAssignTablesReservation = createSelector(selectTablesPlanState, state => state.assignTablesReservation);
export const selectShowSurname = createSelector(selectTablesPlanState, state => state.showSurname);
export const selectShowProgress = createSelector(selectTablesPlanState, state => state.showProgress);
export const selectShowTablesConnection = createSelector(selectTablesPlanState, state => state.showTablesConnection);
export const selectWorkingDate = createSelector(selectTablesPlanState, state => state.workingDate);
export const selectWorkingDateCopy = createSelector(selectTablesPlanState, state => state.workingDateCopy);
export const selectSections = createSelector(selectTablesPlanState, state => state.sections);
export const selectTables = createSelector(selectTablesPlanState, state => state.tables);
export const selectTablesCopy = createSelector(selectTablesPlanState, state => state.tablesCopy);
export const selectSelectedSection = createSelector(selectTablesPlanState, state => state.selectedSection);
export const selectZoom = createSelector(selectTablesPlanState, state => state.zoom);
export const selectSidePanelCollapsed = createSelector(selectTablesPlanState, state => state.sidePanelCollapsed);
export const selectCanvasRect = createSelector(selectTablesPlanState, state => state.canvasRect);
export const selectHasOpenedTablesPlan = createSelector(selectTablesPlanState, state => state.hasOpenedTablesPlan);
export const selectEditingPlan = createSelector(selectTablesPlanState, state => state.editingPlan);
export const selectPendingEditingPlans = createSelector(selectTablesPlanState, state => state.pendingEditingPlans);
export const selectPendingActionsNumber = createSelector(selectTablesPlanState, state => state.pendingEditingPlans.length);
export const selectIsDirty = createSelector(selectTablesPlanState, state => 
  state.editMode === true 
  && state.editingPlan != null 
  && (state.editingPlan.affectedSections.length > 0 || state.editingPlan.affectedTables.length > 0));

// sections filtered by Working Date
export const selectWorkingDateSections = createSelector(
  selectTablesPlanState,
  (state) => {

    if (state.sections == null) {
      return null;
    }

    return state.sections
    .filter(s => s.workingDateId === state.workingDate?.workingDateId)
    .sort((a, b) => {
      if (a.sectionOrder < b.sectionOrder) {
        return -1;
      }
      if (a.sectionOrder > b.sectionOrder) {
        return 1;
      }

      return 0;
    })

  }
);

// tables filtered by Section and Working Date
export const selectSectionTables = createSelector(
  selectTablesPlanState,
  state => (state.tables ?? []).filter(t => {

    // filter connected tables auto assigned from online reservations
    if (t.tableName.includes(',')) {
      return false;
    }

    return t.sectionId === state.selectedSection?.sectionId && t.workingDateId === state.workingDate?.workingDateId;
  })
);

export const selectSectionJoinedTables = createSelector(
  selectTablesPlanState,
  state => (state.tables ?? []).filter(t => {

    // filter connected tables auto assigned from online reservations
    if (!t.tableName.includes(',')) {
      return false;
    }

    return t.sectionId === state.selectedSection?.sectionId && t.workingDateId === state.workingDate?.workingDateId;
  })
  .sort((a, b) => {
    if (a.tableOrder < b.tableOrder) {
      return -1;
    }
    if (a.tableOrder > b.tableOrder) {
      return 1;
    }

    return 0;
  })
);

export const selectSelectedTables = createSelector(selectTablesPlanState, state => state.selectedTables);
export const selectAllocatedTables = createSelector(selectTablesPlanState, state => state.allocatedTables);
export const selectResetAllocatedTablesOnSelection = createSelector(selectTablesPlanState, state => state.resetAllocatedTablesOnSelection);
export const selectEditingTable = createSelector(selectTablesPlanState, state => state.editingTable);
export const selectEditingTableCopy = createSelector(selectTablesPlanState, state => state.editingTableCopy);
export const selectTablesMode = createSelector(selectTablesPlanState, state => state.tablesMode);
export const selectSettings = createSelector(selectTablesPlanState, state => state.settings);

// reservations filtered by Shift
export const selectReservationsFilteredByShift = createSelector(
  selectTablesPlanState,
  selectReservationListState,
  (state, reservationListState) => reservationListState.reservations.filter(r => {
    if (!Common.timeBelongsToShift(r.reservationDetails.arrivalTime, reservationListState.selectedShift)) {
      return false;
    }

    return true;
  })
);

// reservations filtered by Shift and selected tables
export const selectReservationsFilteredBySelectedTables = createSelector(
  selectTablesPlanState,
  selectReservationListState,
  (state, reservationListState) => reservationListState.reservations.filter(r => {
    if (!Common.timeBelongsToShift(r.reservationDetails.arrivalTime, reservationListState.selectedShift)) {
      return false;
    }

    if (state.selectedTables.length <= 0) { // || state.reservationAssignTablesMode
      return true;
    }

    const selectedTableNames = (state.selectedTables ?? []).map(t => t.tableName);
    const reservationAllocatedTableNames = (r.reservationDetails?.allocatedTables ?? '')
      .split(',')
      .filter(t => !Common.isNullOrEmpty(t));

    return selectedTableNames.some(element => {
      return reservationAllocatedTableNames.includes(element);
    });
  })
  .sort(Common.arrivalTimeComparator)
);

// reservations filtered by Shift and Section
export const selectReservationsFilteredByShiftAndSection = createSelector(
  selectTablesPlanState,
  selectReservationListState,
  (state, reservationListState) => reservationListState.reservations.filter(r => {
    if (!Common.timeBelongsToShift(r.reservationDetails.arrivalTime, reservationListState.selectedShift)) {
      return false;
    }

    if (Common.isNullOrEmpty(r.reservationDetails.allocatedTables)) {
      return false;
    }

    const tableNames = r.reservationDetails.allocatedTables
      .split(',')
      .filter(n => !Common.isNullOrEmpty(n));
    
    if (tableNames.length <= 0) {
      return false;
    }

    const tables = state.tables.filter(t => tableNames.includes(t.tableName));
    
    if (tables.length <= 0) {
      return false;
    }

    const firstTable = tables[0];
    const tableSectionId = firstTable.sectionId;

    return tableSectionId === state.selectedSection?.sectionId;
  })
);

export const selectAttendanceRate = createSelector(
  selectSettings,
  settingsState => settingsState?.settings?.attendanceRate ?? 0
);

